import { AiIssuedKey, AiKeyRevocationResult } from '../../api';
import { ToastContext } from '../../services/toast/toast';
import { BaseButton } from '../base/button/button';
import { BaseDialog } from '../base/dialog/dialog';
import { BaseInput } from '../base/input/input';
import { mdiAlert, mdiContentCopy, mdiFlaskOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { humanSeconds } from '../../services/ai/ai.derive';
import { describeAiError as describeError } from '../../services/ai/ai-error';
import { AiStore } from '../../stores/ai/ai.store';
import { formatWhen, SelectField, toNumber, useFieldIds } from './ai-fields';
import * as S from './ai.styles';

const UNSAVED_DRAFT_MESSAGE =
    'You have unsaved configuration changes. Save or discard them first: disabling issuance saves the account configuration.';

/* ---- issue ---- */

interface IIssueDialogProps {
    store: AiStore;
    onClose: () => void;
    onIssued: (key: AiIssuedKey) => void;
}

export const IssueKeyDialog = observer(({ store, onClose, onIssued }: IIssueDialogProps): React.ReactElement => {
    const [profileId, setProfileId] = useState('');
    const [lifetime, setLifetime] = useState<number | ''>('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const lifetimeId = useFieldIds(false).id;

    // The profiles a client is offered right now — the SAVED configuration, read fresh, never the draft.
    useEffect(() => {
        void store.refreshAccess();
    }, [store]);

    const profiles = store.access?.enabled ? store.access.profiles : [];
    const selected = profiles.find(p => p.id === (profileId || profiles[0]?.id));
    const effectiveId = selected?.id ?? '';

    const submit = async (): Promise<void> => {
        if (!selected || busy) return;
        const seconds = lifetime === '' ? undefined : lifetime;
        if (seconds !== undefined && (!Number.isInteger(seconds) || seconds < 60 || seconds > selected.maxLifetimeSeconds)) {
            setError(`400 — lifetime must be 60–${selected.maxLifetimeSeconds} seconds for this profile.`);
            return;
        }
        setBusy(true);
        setError('');
        const { key, error: err } = await store.issueKey(effectiveId, seconds);
        setBusy(false);
        if (key) {
            onIssued(key);
        } else if (err) {
            setError(
                err.status === 503 || err.status === undefined
                    ? `${describeError(err)} No key was confirmed. Issuance is not idempotent: a retry creates a new key.`
                    : describeError(err),
            );
        }
    };

    return (
        <BaseDialog
            open={true}
            // Issuing cannot be cancelled: the request is already out and the server will create the key. Closing now
            // would not stop it, it would only throw away the one chance to be shown the secret, and a late answer
            // would reopen a dialog the operator believed they had dismissed. So Escape, a click outside and Cancel
            // all wait until the answer is in.
            onClose={busy ? undefined : onClose}
            disableBackdropClick={busy}
            renderTitle="Issue key"
            renderActions={
                <S.DialogActions>
                    <S.Button variant="outlined" disabled={busy} onClick={onClose}>
                        Cancel
                    </S.Button>
                    <S.Button variant="primary" disabled={!selected || busy} onClick={(): void => void submit()}>
                        {busy ? 'Issuing…' : 'Issue key'}
                    </S.Button>
                </S.DialogActions>
            }
        >
            <S.DialogBody>
                {store.accessError ? (
                    <S.ErrorText>{store.accessError}</S.ErrorText>
                ) : store.access === undefined ? (
                    <S.Muted>Loading available profiles…</S.Muted>
                ) : (
                    <SelectField
                        label={`Profile · from GET /${store.mode}/ai`}
                        value={effectiveId}
                        options={
                            profiles.length
                                ? profiles.map(p => ({ value: p.id, label: `${p.displayName} (${p.id}) · ${p.targetType}` }))
                                : [{ value: '', label: 'No available profiles' }]
                        }
                        onChange={(v): void => {
                            setProfileId(v);
                            setError('');
                        }}
                    />
                )}
                <S.Field>
                    <S.Label htmlFor={lifetimeId}>Lifetime (seconds, optional)</S.Label>
                    <BaseInput
                        id={lifetimeId}
                        type="number"
                        min={60}
                        max={3600}
                        value={lifetime}
                        placeholder={selected ? `Default ${selected.defaultLifetimeSeconds} · max ${selected.maxLifetimeSeconds}` : ''}
                        onChange={(e): void => {
                            setLifetime(toNumber(e.target.value));
                            setError('');
                        }}
                    />
                </S.Field>
                {error && <S.ErrorText>{error}</S.ErrorText>}
                {busy && <S.Muted role="status">Issuing the key. It cannot be cancelled once sent, so this window stays open until it answers.</S.Muted>}
                <S.Hint>
                    {store.dirty ? 'Uses the saved configuration, not unsaved draft changes. ' : ''}
                    Each issue creates a distinct key under the shared account budget.{' '}
                    {store.mode === 'support'
                        ? 'Recorded with you as the support actor.'
                        : 'The key is yours: only you can look it up or revoke it.'}
                </S.Hint>
            </S.DialogBody>
        </BaseDialog>
    );
});

/* ---- secret (shown once) ---- */

interface ISecretDialogProps {
    issued: AiIssuedKey;
    onClose: () => void;
    onOpenTesting: () => void;
}

export const KeySecretDialog = ({ issued, onClose, onOpenTesting }: ISecretDialogProps): React.ReactElement => {
    const toast = useContext(ToastContext);
    const [copied, setCopied] = useState(false);

    const copy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(issued.apiKey);
            setCopied(true);
        } catch {
            toast.notify?.('Failed to copy to clipboard', 'right', 'warning');
        }
    };

    return (
        <BaseDialog
            open={true}
            // Closing is the only way out: the secret is not recoverable afterwards.
            onClose={onClose}
            renderTitle="Key issued"
            renderActions={
                <S.DialogActions>
                    <S.Button variant="outlined" onClick={onOpenTesting}>
                        <S.ButtonContent>
                            <Icon path={mdiFlaskOutline} size={0.75} />
                            Open in Testing
                        </S.ButtonContent>
                    </S.Button>
                    <S.Button variant="primary" onClick={onClose}>
                        Done
                    </S.Button>
                </S.DialogActions>
            }
        >
            <S.DialogBody>
                <S.PageBanner tone="warning" title="Shown once. Not recoverable after closing.">
                    Keep it in memory and out of logs and URLs.
                </S.PageBanner>
                <S.Field>
                    <S.Label>API key</S.Label>
                    <S.InlineRow>
                        <S.SecretBox>{issued.apiKey}</S.SecretBox>
                        <S.Button variant="outlined" onClick={(): void => void copy()}>
                            <S.ButtonContent>
                                <Icon path={mdiContentCopy} size={0.75} />
                                {copied ? 'Copied' : 'Copy'}
                            </S.ButtonContent>
                        </S.Button>
                    </S.InlineRow>
                </S.Field>
                <S.KV>
                    <span>Base URL</span>
                    <S.Mono>{issued.baseUrl}</S.Mono>
                    <span>Default model</span>
                    <S.Mono>{issued.defaultModel}</S.Mono>
                    <span>Models</span>
                    <S.Mono>{issued.models.join(', ')}</S.Mono>
                    <span>Key ID</span>
                    <S.Mono>{issued.id}</S.Mono>
                    <span>Expires</span>
                    <span>
                        {formatWhen(issued.expiresAt)} ({humanSeconds(issued.expiresInSeconds)})
                    </span>
                </S.KV>
            </S.DialogBody>
        </BaseDialog>
    );
};

/* ---- revoke ---- */

interface IRevokeDialogProps {
    store: AiStore;
    initialScope: string;
    onClose: () => void;
    onRevoked: (result: AiKeyRevocationResult) => void;
}

export const RevokeKeysDialog = observer(({ store, initialScope, onClose, onRevoked }: IRevokeDialogProps): React.ReactElement => {
    const [scope, setScope] = useState(initialScope);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const savedIds = store.saved?.profiles.map(p => p.id) ?? [];
    // A removed profile can still have live keys, so keys we have seen name profiles the config no longer lists.
    // `initialScope` too: after saving a profile's removal the banner opens this dialog on that ID, which may have no
    // tracked key. Without it the selector would show nothing while Submit still revoked that profile's keys.
    const ids = Array.from(
        new Set([...(initialScope ? [initialScope] : []), ...savedIds, ...store.tracked.map(k => k.profileId)]),
    );
    const scopeProfile = scope ? store.saved?.profiles.find(p => p.id === scope) : undefined;
    const issuanceOn = !!(
        store.saved?.enabled &&
        (scope ? scopeProfile?.enabled : store.saved.profiles.some(p => p.enabled))
    );

    const disableFirst = async (): Promise<void> => {
        setError('');
        // Saves the SAVED configuration with issuance switched off, which would replace (and so lose) any unsaved edits
        // in the draft. The store refuses in that case rather than discard them silently.
        const res = await store.disableIssuance(scope || undefined);
        if (res.blockedByDraft) setError(UNSAVED_DRAFT_MESSAGE);
        else if (!res.ok && store.banner) setError(`${store.banner.title} ${store.banner.body ?? ''}`.trim());
    };

    const submit = async (): Promise<void> => {
        // The store refuses too; this keeps a stale click from even showing a request in flight.
        if (busy || store.saving) return;
        setBusy(true);
        setError('');
        const { result, error: err } = await store.revokeKeys(scope || undefined);
        setBusy(false);
        if (result) onRevoked(result);
        else if (err) setError(err.status === 503 ? `${describeError(err)} Revocation may be partial. Retrying is safe.` : describeError(err));
    };

    return (
        <BaseDialog
            open={true}
            // Not dismissible while the request is out: a late success would otherwise call `onRevoked` and close
            // whichever dialog the operator opened since.
            onClose={busy ? (): void => undefined : onClose}
            renderTitle="Revoke keys"
            renderActions={
                <S.DialogActions>
                    <S.Button variant="outlined" disabled={busy} onClick={onClose}>
                        Cancel
                    </S.Button>
                    {/* Not while "Disable & save" is still saving: the cutoff must be taken after issuance is off. */}
                    <S.Button variant="warning" disabled={busy || store.saving} onClick={(): void => void submit()}>
                        {busy ? 'Revoking…' : store.saving ? 'Waiting for save…' : 'Revoke keys'}
                    </S.Button>
                </S.DialogActions>
            }
        >
            <S.DialogBody>
                <SelectField
                    label="Scope"
                    value={scope}
                    // Fixed while a revoke or "Disable & save" is out: the result belongs to the scope it was sent for.
                    disabled={busy || store.saving}
                    options={[
                        { value: '', label: 'All profiles' },
                        ...ids.map(id => ({ value: id, label: id + (savedIds.includes(id) ? '' : ' (removed)') })),
                    ]}
                    onChange={(v): void => {
                        setScope(v);
                        setError('');
                    }}
                />
                {issuanceOn && (
                    <S.PageBanner
                        tone="warning"
                        title="Issuance is still on for this scope."
                        action={
                            <BaseButton
                                variant="outlined"
                                size="compact"
                                disabled={store.dirty || store.saving || busy}
                                onClick={(): void => void disableFirst()}
                            >
                                Disable &amp; save
                            </BaseButton>
                        }
                    >
                        <Icon path={mdiAlert} size={0.6} /> Keys issued after this request starts are not revoked. Disable
                        issuance first to shut off access.
                        {store.dirty && ` ${UNSAVED_DRAFT_MESSAGE}`}
                    </S.PageBanner>
                )}
                {error && <S.ErrorText>{error}</S.ErrorText>}
                {store.saving && (
                    <S.Muted role="status">
                        Saving the configuration. Keys can be revoked once it has finished, with issuance already off.
                    </S.Muted>
                )}
                <S.Hint>
                    Revokes all unexpired Enter-issued keys for this account and environment, regardless of issuing
                    actor. Spend is not reset. In-flight inference is not cancelled. Safe to retry on 503.
                </S.Hint>
            </S.DialogBody>
        </BaseDialog>
    );
});

/* ---- confirm empty ---- */

export const ConfirmEmptyDialog = ({
    count,
    onCancel,
    onConfirm,
}: {
    count: number;
    onCancel: () => void;
    onConfirm: () => void;
}): React.ReactElement => (
    <BaseDialog
        open={true}
        onClose={onCancel}
        renderTitle="Remove all profiles?"
        renderActions={
            <S.DialogActions>
                <S.Button variant="outlined" onClick={onCancel}>
                    Cancel
                </S.Button>
                <S.Button variant="warning" onClick={onConfirm}>
                    Remove all profiles
                </S.Button>
            </S.DialogActions>
        }
    >
        <S.DialogBody>
            <span>
                Saving removes all {count} profile{count === 1 ? '' : 's'} and stops new issuance for each.
            </span>
            <S.Hint>
                An explicit empty profiles list means no use case is available. Existing keys stay valid until expiry or
                revocation.
            </S.Hint>
        </S.DialogBody>
    </BaseDialog>
);
